import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { TrendingUp, DollarSign } from "lucide-react";

/* 🔥 DATA */
const points = [
  { price: 189, demand: 30, label: "Low demand" },
  { price: 204, demand: 50, label: "Rising demand" },
  { price: 221, demand: 65, label: "Stable demand" },
  { price: 239, demand: 80, label: "High demand" },
  { price: 248, demand: 95, label: "Peak demand" },
];

export default function FeatureVisualPricing() {

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const i = setInterval(() => {
      setIndex(i => (i + 1) % points.length);
    }, 2600);
    return () => clearInterval(i);
  }, []);

  const current = points[index];

  return (
    <div className="w-full h-full bg-bg-surface rounded-xl p-6 flex flex-col justify-between overflow-hidden">

      {/* 🔝 HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-small font-semibold text-text-primary">
            Dynamic pricing
          </p>
          <p className="text-micro text-text-muted">
            Demand-driven optimisation
          </p>
        </div>

        <motion.span
          className="text-micro text-brand-primary"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          Live
        </motion.span>
      </div>

      {/* 🔥 CENTER ZONE (FIXED BALANCE) */}
      <div className="flex-1 flex flex-col justify-center gap-6">

        {/* 📈 GRAPH (NOW CENTERED) */}
        <div className="relative h-[120px] flex items-center justify-center">

          <div className="relative w-full max-w-[260px] h-full">

            {/* BASE LINE */}
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-border-soft" />

            {/* POINTS */}
            <div className="absolute inset-0 flex items-end justify-between px-2">

              {points.map((p, i) => {
                const isActive = i === index;

                return (
                  <div key={i} className="relative flex flex-col items-center">

                    <motion.div
                      className={`
                        w-2.5 h-2.5 rounded-full
                        ${isActive ? "bg-brand-primary" : "bg-border-soft"}
                      `}
                      animate={{ scale: isActive ? 1.4 : 1 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        marginBottom: `${p.demand * 0.7}px`,
                      }}
                    />

                  </div>
                );
              })}

            </div>

            {/* ACTIVE GLOW */}
            <motion.div
              key={index}
              className="absolute w-5 h-5 rounded-full bg-brand-primary/20"
              animate={{
                left: `${(index / (points.length - 1)) * 100}%`,
                bottom: `${current.demand * 0.7}px`,
              }}
              transition={{ duration: 0.5 }}
            />

          </div>

        </div>

        {/* 💰 PRICE BLOCK (ENRICHED) */}
        <div className="h-[110px] flex items-center justify-center text-center relative">

          <AnimatePresence mode="wait">
            <motion.div
              key={current.price}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="absolute flex flex-col items-center gap-1"
            >

              <div className="flex items-center gap-2 text-text-muted text-micro">
                <DollarSign size={14} />
                Suggested nightly rate
              </div>

              <div className="text-[36px] font-bold text-text-primary">
                ${current.price}
              </div>

              <div className="flex items-center gap-1 text-small text-brand-primary font-medium">
                <TrendingUp size={14} />
                {current.label}
              </div>

            </motion.div>
          </AnimatePresence>

        </div>

      </div>

      {/* 🔽 FOOTER */}
      <div className="flex justify-between items-center mt-2">

        <span className="text-micro text-text-muted">
          Auto optimisation active
        </span>

        <div className="flex items-center gap-2 text-micro text-brand-primary font-semibold">
          <TrendingUp size={14} />
          +32% revenue
        </div>

      </div>

    </div>
  );
}